import type { NavItem } from './layout';

/**
 * BL-297: Ablösung der BL-284-Icon-Schiene + separatem Bereichs-Panel durch
 * eine einzige, klassische Sidebar mit kollabierbaren Gruppen (User-Feedback
 * direkt am Live-Stand, gleicher Tag wie BL-284 — zwei Ebenen brauchten zu
 * viele Klicks). Diese Datei bündelt die reine Gruppierungslogik (kein
 * React/DOM), analog zum bisherigen `sectionForPath()`-Test-Stil.
 *
 * Mapping bestehender `NavItem.section`-Werte auf die vier Sidebar-Gruppen
 * aus dem User-Mockup. "Start" bleibt bewusst außerhalb jeder Gruppe (ein
 * einzelner Top-Link, kein Header nötig für einen einzelnen Eintrag).
 * "Auswertung" (Personalcontrolling) und "Kanzlei" (FiBu/LoBu/OPOS/USt-VA)
 * werden zu EINER Gruppe zusammengeführt — Kanzlei-Einträge dort als
 * kompakte Chip-Reihe statt vier Vollbreite-Zeilen (siehe `chipSection`).
 */
export interface SidebarGroup {
  header: string;
  items: NavItem[];
  /** Items dieser Section innerhalb der Gruppe als Chip-Reihe rendern statt als Liste. */
  chipItems: NavItem[];
  defaultOpen: boolean;
}

const GROUP_CONFIG: Array<{
  header: string;
  sections: string[];
  chipSections?: string[];
  defaultOpen: boolean;
}> = [
  { header: 'ABRECHNUNG', sections: ['Abrechnung'], defaultOpen: true },
  { header: 'STAMMDATEN', sections: ['Stammdaten'], defaultOpen: true },
  {
    header: 'KANZLEI & AUSWERTUNG',
    sections: ['Auswertung'],
    chipSections: ['Kanzlei'],
    defaultOpen: true,
  },
  { header: 'SYSTEM', sections: ['System'], defaultOpen: false },
];

/** Persistenz-Defaults, exportiert für den Sidebar-State (localStorage-Seed). */
export const GROUP_DEFAULT_OPEN: Record<string, boolean> = Object.fromEntries(
  GROUP_CONFIG.map((g) => [g.header, g.defaultOpen]),
);

/**
 * Gruppiert die (bereits BL-263-gefilterten) sichtbaren Nav-Items. "Start"
 * wird separat zurückgegeben (kein Gruppen-Header), der Rest nach obigem
 * Mapping. Eine Gruppe ohne sichtbare Items (z.B. komplett gesperrter
 * Bereich) wird weggelassen, keine leere Überschrift.
 */
export function buildSidebarGroups(items: NavItem[]): {
  start: NavItem | undefined;
  groups: SidebarGroup[];
} {
  const start = items.find((i) => i.section === 'Start');
  const groups: SidebarGroup[] = [];
  for (const config of GROUP_CONFIG) {
    const groupItems = items.filter((i) => i.section && config.sections.includes(i.section));
    const chipItems = items.filter((i) => i.section && config.chipSections?.includes(i.section));
    if (groupItems.length === 0 && chipItems.length === 0) continue;
    groups.push({
      header: config.header,
      items: groupItems,
      chipItems,
      defaultOpen: config.defaultOpen,
    });
  }
  return { start, groups };
}

/**
 * BL-298: überschreibt `badge`/`badgeTone` einzelner Items anhand ihres
 * `to`-Pfads (z.B. dynamische Zähler aus `GET /api/dashboard/nav-badges`).
 * Reine Funktion, damit sie unabhängig vom Fetch-State testbar bleibt --
 * `AppSidebar` ruft sie auf `allItems` auf, bevor `buildSidebarGroups`
 * gruppiert.
 */
export function applyNavBadges(
  items: NavItem[],
  badges: Record<string, { value: string; tone?: 'default' | 'danger' }>,
): NavItem[] {
  return items.map((item) => {
    const override = badges[item.to];
    if (!override) return item;
    return { ...item, badge: override.value, badgeTone: override.tone };
  });
}

/** Header der Gruppe, die den aktuellen Pfad enthält -- undefined für "Start" oder unbekannte Pfade. */
export function groupHeaderForPath(groups: SidebarGroup[], pathname: string): string | undefined {
  for (const { header, items, chipItems } of groups) {
    for (const item of [...items, ...chipItems]) {
      if (item.to === '/' ? pathname === '/' : pathname.startsWith(item.to)) return header;
    }
  }
  return undefined;
}

/**
 * BL-285: welches NavItem "gehört" zu einem beliebigen Pfad -- inkl.
 * Detail-/Unterseiten wie `/orderbuch/123`, die selbst kein eigenes NavItem
 * haben, aber als Präfix zu einem gehören. Gleiche Matching-Regel wie
 * `groupHeaderForPath` (Start nur exakt, sonst Präfix), hier aber auf der
 * flachen `items`-Liste statt auf Gruppen -- Basis für den "Zuletzt
 * besucht"-Verlauf, der unabhängig von der Sidebar-Gruppierung ist.
 */
export function navItemForPath(items: NavItem[], pathname: string): NavItem | undefined {
  return items.find((item) => (item.to === '/' ? pathname === '/' : pathname.startsWith(item.to)));
}
